
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function PatioDeckLightingPage() {
  const siteUrl =
    import.meta.env.VITE_SITE_URL ||
    'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Patio & Deck Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional patio and deck lighting installation in Fresno, CA. Create perfect outdoor entertaining spaces with custom lighting design.',
    url: `${siteUrl}/services/lighting-outdoor/patio-deck-lighting`,
  };

  const features = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Ambient Lighting',
      description:
        'Create warm, inviting atmosphere for outdoor entertaining',
    },
    {
      icon: 'ri-stairs-line',
      title: 'Step & Rail Lighting',
      description: 'Safe illumination for deck stairs and railings',
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Dining Area Lighting',
      description: 'Perfect lighting for outdoor dining and gatherings',
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Dimmable Controls',
      description: 'Adjust brightness for any occasion or time of day',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Post Cap Lights',
      description: 'Decorative lighting on deck posts and columns',
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Smart Lighting',
      description: 'Control patio lights with apps or voice commands',
    },
  ];

  return (
    <>
      <SEOHead
        title="Patio & Deck Lighting Fresno CA | Outdoor Entertainment Lighting"
        description="Professional patio and deck lighting installation in Fresno, CA. Create perfect outdoor entertaining spaces with custom lighting design. Licensed electricians."
        canonical="/services/lighting-outdoor/patio-deck-lighting"
        keywords="patio lighting Fresno, deck lighting, outdoor entertainment lighting, pergola lights"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Patio & Deck Lighting — Fresno, CA"
        heroSubtitle="Transform your Fresno outdoor living spaces with beautiful patio and deck lighting"
        heroImage="https://public.readdy.ai/ai/img_res/2d3ed2841cc0b30c41abccc84df6e191.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20modern%20LED%20deck%20rail%20lighting%20and%20post%20cap%20lights%20illuminating%20wooden%20deck%20at%20night%20with%20warm%20amber%20glow%20California%20home%20no%20people%20present%20professional%20installation&width=700&height=600&seq=patio-deck-fresno-011&orientation=portrait"
        fresnoContent="Extend your Fresno outdoor living season with professionally designed patio and deck lighting. From Tower District bungalows to Woodward Park estates, we create custom lighting systems that transform outdoor spaces into beautiful, functional entertainment areas. Our licensed electricians install energy-efficient LED fixtures, string lights, step lighting, and smart controls that withstand Central Valley heat. Enjoy your patio and deck safely after dark with lighting that enhances ambiance, safety, and property value."
        features={features}
        faqs={[
          {
            question: 'What types of patio and deck lighting work best in Fresno?',
            answer:
              "For Fresno patios and decks, we recommend a layered lighting approach: ambient lighting from string lights or overhead fixtures for general illumination, task lighting for dining and cooking areas, accent lighting to highlight architectural features or landscaping, and safety lighting for steps, stairs, and railings. LED fixtures are ideal for Fresno's climate—they handle extreme heat, use 75% less energy, and last 25+ years. Popular options include string lights, recessed deck lights, post cap lights, and wall-mounted fixtures.",
          },
          {
            question: 'How much does patio and deck lighting installation cost in Fresno?',
            answer:
              'Patio and deck lighting installation in Fresno typically costs $1,500–$5,000 depending on size and complexity. Basic deck lighting with 8–12 recessed step lights and post caps starts around $1,200–$2,000. Comprehensive patio systems with string lights, wall fixtures, and smart controls range from $3,000–$6,000. Prices include all fixtures, wiring, weatherproofing, and installation. We provide free design consultations and detailed quotes for all Fresno projects. Most homeowners see immediate ROI through increased outdoor living enjoyment and property value.',
          },
          {
            question: 'Can you install string lights on patios and pergolas in Fresno?',
            answer:
              'Yes! We specialize ... (truncated for brevity) ...',
          },
          {
            question: 'Should deck lighting be on dimmers in Fresno?',
            answer:
              'Absolutely! We strongly recommend dimmer controls for Fresno deck and patio lighting. Dimmers allow you to adjust brightness for different activities—bright lighting for cooking and dining, medium lighting for casual gatherings, and low ambient lighting for relaxing evenings. Dimming also extends bulb life and saves energy. We install LED-compatible dimmers that provide smooth, flicker-free control. Many Fresno homeowners also choose smart dimmers for app and voice control through Alexa or Google Home.',
          },
          {
            question: 'How do you weatherproof patio and deck lighting in Fresno?',
            answer:
              'All our Fresno patio and deck lighting installations use weatherproof fixtures and materials rated for outdoor use. We install fixtures with IP65 or higher ratings that protect against dust, rain, and moisture. All electrical connections are made in weatherproof junction boxes with sealed wire nuts. We use UV-resistant wiring and conduit that withstand California sun exposure. Fixtures are rated for temperatures exceeding 110°F, perfect for Fresno summers. Proper weatherproofing ensures reliable performance and long fixture life.',
          },
          {
            question: 'Do patio and deck lights require permits in Fresno?',
            answer:
              "Yes, most patio and deck lighting installations in Fresno require electrical permits since they involve modifying your home's electrical system. Low-voltage (12V) systems may have different requirements than 120V installations. We handle all permit applications and inspections with the City of Fresno, ensuring full compliance with California electrical codes. Our licensed C-10 electricians ensure all installations meet Fresno building standards. Proper permitting protects your home value and ensures insurance coverage.",
          },
        ]}
      />
    </>
  );
}
